import { useAction } from 'hooks'
import { isEmpty } from 'lodash'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { Box, Checkbox, FormControlLabel } from '@material-ui/core'
import { setErrorMessage, showDeleMediaFilesFormAction } from 'actions/mainView'
import Scrollbars from 'components/base/Scrollbars'
import Button from 'components/base/Button'
import { TranslationContext } from 'contexts/TranslationContext'
import './DeleteMediaFiles.scss'
import { useSelector } from 'react-redux'
import { selectSelectedSourceMediaFileIds } from 'selectors/mainView'
import { getSourceFilesByIds } from 'selectors'
import { MediaAsset } from 'models/Asset'
import UncheckedIcon from 'components/base/Checkbox/UnchekedIcon'
import CheckedIcon from 'components/base/Checkbox/CheckedIcon'
import { CircularProgress } from 'components/base/CircularProgress/CircularProgress'
import { requestDeleteFile } from 'actions/sourceFiles'
import { SOURCE_FILE_TYPES } from 'enums'
import classNames from 'classnames'
import { ReactComponent as CloseIcon } from '~/assets/settings/navigation_close.svg'
import { ProjectData, ProjectMediaFilesMap } from './types'

interface IProps {
  visible: boolean
  projectMediaFiles: ProjectMediaFilesMap
  setProjectMediaFiles: React.Dispatch<React.SetStateAction<ProjectMediaFilesMap>>
}

export const DeleteMediaFilesComponent: FC<IProps> = ({ visible, projectMediaFiles, setProjectMediaFiles }) => {
  const selectedMediaFileIds = useSelector(selectSelectedSourceMediaFileIds)
  const onCloseWindow = useAction(showDeleMediaFilesFormAction, { visibility: false })
  const onSetErrorMessage = useAction(setErrorMessage)
  const onRequestDeleteMedia = useAction(requestDeleteFile)
  const files: MediaAsset[] = useSelector(state => getSourceFilesByIds(state, selectedMediaFileIds))
  const [ isLoading, setLoading ] = useState<boolean>(false)
  const { t } = useContext(TranslationContext)
  const abortController = useRef<null | AbortController>(null)

  const onDeleteFiles = async () => {
    if (!abortController.current) {
      abortController.current = new AbortController()
    }
    setLoading(true)
    const presenceFiles: typeof projectMediaFiles = {}
    try {
      for (const file of files) {
        // eslint-disable-next-line
        const { projects } = await onRequestDeleteMedia(file.id, SOURCE_FILE_TYPES.MEDIA, false, abortController.current)
        if (!isEmpty(projects)) {
          presenceFiles[file.id] = { file, checked: true, projects }
        }
      }
    } catch (e) {
      abortController.current?.abort()
      setLoading(false)
      onCloseWindow()
    }
    setLoading(false)
    if (!isEmpty(presenceFiles)) {
      setProjectMediaFiles(() => ({ ...presenceFiles }))
    } else {
      onCloseWindow()
    }
  }

  const selectFile = (value: boolean, fileId: string) => {
    setProjectMediaFiles(prev => ({ ...prev, [fileId]: { ...prev[fileId], checked: value } }))
  }

  const cancel = () => {
    abortController.current?.abort()
    onCloseWindow()
  }

  const deleteMedia = async () => {
    if (!abortController.current) {
      abortController.current = new AbortController()
    }
    setLoading(true)
    const excludeIds = Object.keys(projectMediaFiles).filter(key => !projectMediaFiles[key].checked)
    const filesToDelete = isEmpty(excludeIds) ? files : files.filter(file => !excludeIds.includes(file.id))
    const ids = filesToDelete.map(file => file.id)
    try {
      for (const id of ids) {
        // eslint-disable-next-line
        await onRequestDeleteMedia(id, SOURCE_FILE_TYPES.MEDIA, true, abortController.current)
      }
    } catch (e) {
      abortController.current?.abort()
      setLoading(false)
      onCloseWindow()
      onSetErrorMessage(t('MEDIA_ERROR_not_found'))
    }
    onCloseWindow()
    setLoading(false)
  }

  useEffect(() => {
    onDeleteFiles()
    return () => {
      setProjectMediaFiles({})
      abortController.current?.abort()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!visible) return null

  return (
    <Box className="delete-media-form allow-draggable" height="100%" alignItems="center">
      <div className="delete-media-form__close" onClick={() => onCloseWindow()}>
        <CloseIcon />
      </div>
      <Box padding="0 48px" height="100%" className="allow-draggable">
        <h2 className="allow-draggable">{t('MEDIA_FILES_DELETE_FORM_TITLE')}</h2>
        <div className="delete-media-form__desc allow-draggable">{t('MEDIA_FILES_DELETE_FORM_DESC')}</div>
        <div className={classNames('delete-media-form__scroll-container', {
          'no-border': isLoading,
        })}
        >
          {isLoading ? (
            <CircularProgress
              size={100}
              endless
              percents={50}
              transparent
              text={undefined}
              progress={0}
            />
          ) : (
            <>
              <div className="delete-media-form__scroll-head">
                <Box paddingLeft="16px">
                  <div className="delete-media-form__caption">{t('MEDIA_FILES_DELETE_FORM_SELECT_CAPTION')}</div>
                </Box>
              </div>
              <div>
                <Box paddingLeft="15px">
                  <Scrollbars>
                    {Object.keys(projectMediaFiles).map(fileId => (
                      <Box className="delete-media-form__file-item">
                        <FormControlLabel
                          key={fileId}
                          className="delete-media-form__label"
                          control={(
                            <Checkbox
                              onChange={(_, value) => selectFile(value, fileId)}
                              color="primary"
                              name={fileId}
                              checkedIcon={<CheckedIcon />}
                              icon={<UncheckedIcon stroke="#A2A2A2" />}
                              checked={projectMediaFiles[fileId].checked}
                              defaultChecked={projectMediaFiles[fileId].checked}
                            />
                          )}
                          label={projectMediaFiles[fileId].file.name}
                        />
                        <Box position="relative" top="-5px" height="100%" className="delete-media-form__project-info">
                          {projectMediaFiles[fileId].projects.reduce((str: string, project: ProjectData, index) => {
                            const comma = index ? ',' : ''
                            return `${str} ${comma} ${project.name} (${(
                              new Date(project.modifTime)).toLocaleDateString()})`
                          }, 'Projects:')}
                        </Box>
                      </Box>
                    ))}
                  </Scrollbars>
                </Box>
              </div>
            </>
          )}
        </div>
        <Box display="flex" justifyContent="center" className="delete-media-form__footer">
          <Button
            type="submit"
            className="btn--border delete-media-form__button"
            primary
            onClick={cancel}
            autoFocus
          >
            {t('MEDIA_CANCEL_BTN_DELETE')}
          </Button>
          <Button
            type="submit"
            className="delete-media-form__button btn btn--secondary"
            primary
            onClick={deleteMedia}
            autoFocus
          >
            {t('MEDIA_CONFIRM_BTN_DELETE')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
